import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  ViewChild,
  ViewChildren
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, UntypedFormGroup } from '@angular/forms';

import { pdfDefaultOptions } from 'ngx-extended-pdf-viewer';
import { MessageService } from 'primeng/api';
import { Button } from 'primeng/button';
import { DialogService } from 'primeng/dynamicdialog';
import { FileSelectEvent, FileUpload, FileUploadModule } from 'primeng/fileupload';
import { Image } from 'primeng/image';
import { ToastModule } from 'primeng/toast';
import {
  BehaviorSubject,
  Observable,
  Subject,
  firstValueFrom,
  map,
  of,
  pairwise,
  skipWhile,
  startWith,
  take,
  takeUntil
} from 'rxjs';

import {
  DefaultIcon,
  EnumSelect,
  FileContentType,
  FileContentTypeMapping,
  FileReference,
  FileType,
  IFileReference,
  isString
} from '@summit/core';
import { DisplaySchema, SummitDisposing, SummitIdObject, ViewField } from '@summit/models';
import { SummitCrudService } from '@summit/services';
import { ApiError, BlurDirective } from '@summit/ui';

import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { PdfLibraryService } from '@summit/services';
import { Message } from 'primeng/message';
import { Skeleton } from 'primeng/skeleton';
import { SummitFile } from '../../models/summit-file';
import { SFormInputEnumComponent } from '../s-form-input-enum';
import { SFormPdfPreviewDialogComponent } from '../s-form-pdf-preview-dialog/s-form-pdf-preview-dialog.component';
@Component({
  selector: 's-form-file-upload',
  standalone: true,

  imports: [
    AsyncPipe,
    FileUploadModule,
    Button,
    Image,
    SFormInputEnumComponent,
    ReactiveFormsModule,
    ToastModule,
    BlurDirective,
    NgTemplateOutlet,
    Message,
    Skeleton
  ],
  providers: [MessageService],
  templateUrl: './s-form-input-file.component.html',
  styleUrls: ['./s-form-input-file.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SFormInputFileComponent<T extends SummitIdObject>
  extends SummitDisposing
  implements OnInit, AfterViewInit, OnDestroy
{
  lclFormGroup: UntypedFormGroup;

  @Input() apiError: ApiError;
  @Input() form: UntypedFormGroup;
  @Input() contentTypes: FileContentType[] = [];
  //form: UntypedFormGroup;
  @Input() schema: DisplaySchema<T> | false;
  @Input() disablePdfPreview = false;
  @Input() multiple$: Observable<boolean> = of(true);
  @Input() accept = '.jpg,.png,.pdf,.jpeg,.docx';
  @Input() autoSaveEnabled = false; //paired with refreshEvent to work around upload issue
  @Input() readonly?: boolean;
  @Output() fileCountChange = new EventEmitter<number>();
  @Output() removeEvent = new EventEmitter<string>();
  @Output() refreshEvent = new EventEmitter();
  fileInputListener: () => void;
  get fileRefs(): IFileReference[] {
    return this._fileRefs.value?.map((x) => new FileReference(x as IFileReference) as IFileReference);
  }
  @Input() set fileRefs(incomingFiles: IFileReference[]) {
    //console.log('Setting file refs', incomingFiles);
    // incomingFiles?.forEach((fileRef) => {
    //   console.log('Adding control', fileRef.path);
    //   console.log('Adding control', fileRef.cleanPath);
    //   this.lclFormGroup.addControl(fileRef.cleanPath, []);
    // });
    this._fileRefs.next(incomingFiles ?? []);
    incomingFiles?.forEach((fileRef) => {
      this.getThumbnail(fileRef);
    });
  }

  private _fileRefs: BehaviorSubject<IFileReference[]> = new BehaviorSubject<IFileReference[]>([]);
  public fileRefs$ = this._fileRefs.asObservable();
  public newFileRefs$ = this.fileRefs$.pipe(map((fileRefs) => fileRefs?.filter((fileRef) => fileRef.isLocal)));
  public fileRefsToDisplay$ = this.fileRefs$.pipe(map((fileRefs) => fileRefs?.filter((fileRef) => !fileRef.isLocal)));
  @ViewChild('fileUpload', { static: false }) fileUpload: FileUpload;
  @ViewChildren('contentTypeInput') contentTypeInputs: SFormInputEnumComponent<T>[];
  thumbnail: File;
  counter = 0;
  fileMap: Map<string, IFileReference> = new Map<string, IFileReference>();
  readonly FileType = FileType;
  contentTypeOptions = EnumSelect.ToIdList(FileContentType);
  contentTypeMapping = FileContentTypeMapping;
  defaultIcon = DefaultIcon;
  attribute: ViewField<T>;

  constructor(
    private crud: SummitCrudService,
    private dialogService: DialogService,
    protected cd: ChangeDetectorRef,
    private httpClient: HttpClient,
    private formBuilder: FormBuilder,
    private renderer: Renderer2,
    private sanitizer: DomSanitizer,
    private messageService: MessageService, //formGroupDirective: FormGroupDirective
    private pdfLibraryService: PdfLibraryService
  ) {
    super();
    //this.form = formGroupDirective.form;
    this.fileRefs$.pipe(startWith([]), takeUntil(this.disposing$)).subscribe((files) => {
      // console.warn('File refs count changed ', files);
      this.fileCountChange.emit(files.length);
    });
    this.lclFormGroup = this.formBuilder.group({});
  }
  get files(): File[] {
    //console.log('Getting files', this.fileUpload);
    return this.fileUpload.files;
  }

  async ngOnInit() {
    // Use CDN with fallback to local assets for better performance and reliability
    // Service tries multiple CDNs and falls back to local if all fail
    pdfDefaultOptions.assetsFolder = await this.pdfLibraryService.getPdfViewerAssetsFolder();

    if (this.contentTypes.length > 0)
      this.contentTypeOptions = EnumSelect.ToIdListFilter(FileContentType, this.contentTypes);
  }

  inputClickListener: EventListener = (_event) => {
    this.clearOldFileListeners();
  };

  ngAfterViewInit() {
    if (this.fileUpload?.advancedFileInput?.nativeElement) {
      this.fileInputListener = this.renderer.listen(
        this.fileUpload.advancedFileInput.nativeElement,
        'click',
        this.inputClickListener
      );
    }
  }

  fileCountChanged($event: FileSelectEvent) {
    // console.warn('fileCountChanged', $event);

    if ($event.originalEvent instanceof DragEvent) {
      this.clearOldFileListeners();
    }
    if (!$event) return;
    const newFiles = $event.currentFiles.filter(
      (file, $index, $array) => $index == $array.findIndex((f) => f.name === file.name)
    );
    if (newFiles.length === 0) return;
    //rename files to avoid duplicates in all current files
    const allFiles = $event.currentFiles;
    const renamedFiles = newFiles.map((file, index) => {
      const duplicateCount = allFiles.filter((f) => f.name === file.name).length;
      if (duplicateCount > 1) {
        const extensionIndex = file.name.lastIndexOf('.');
        const baseName = extensionIndex !== -1 ? file.name.substring(0, extensionIndex) : file.name;
        const extension = extensionIndex !== -1 ? file.name.substring(extensionIndex) : '';
        // Find the occurrence index among all files with the same name
        const occurrenceIndex = allFiles.filter((f, i) => f.name === file.name && i <= index).length;
        const newName = `${baseName}(${occurrenceIndex})${extension}`;
        // Update the name in fileUpload.files array
        const fileIndex = this.fileUpload.files.findIndex((f) => f.name === file.name);
        if (fileIndex !== -1) {
          const updatedFile = new File([file], newName, { type: file.type });
          this.fileUpload.files[fileIndex] = updatedFile;
          return updatedFile;
        }
        return new File([file], newName, { type: file.type });
      }
      return file;
    });

    this.addFileRef(renamedFiles);
  }

  private addFileRef(allFiles: File[]) {
    const currentFileRefs = [...this._fileRefs.value];
    //console.warn('Current file refs', currentFileRefs, allFiles);
    for (let i = 0; i < allFiles.length; i++) {
      const file = allFiles[i];
      //if (currentFileRefs.find((x) => x.name === file.name)) continue;
      let fileType = FileType.Unknown;
      switch (file.type) {
        case 'application/pdf':
          fileType = FileType.PDF;
          break;
        case 'application/json':
          fileType = FileType.JSON;
          break;
        case 'image/png':
          fileType = FileType.PNG;
          break;
        case 'image/jpeg':
          fileType = FileType.JPG;
          break;
        case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
          fileType = FileType.XLSX;
          break;
        case 'text/csv':
          fileType = FileType.CSV;
          break; // ADD THIS MISSING BREAK!
        case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
          fileType = FileType.DOCX;
          console.log('DOCX file detected:', file.name, file.type);
          break;
        default:
          if (file.name.endsWith('.geojson')) fileType = FileType.JSON;
          else console.error(`Unknown file type: ${file.type}`, file);
          break;
      }
      const blobPath = URL.createObjectURL(file);
      const newFileRef = new FileReference({
        name: file.name,
        fileType: fileType,
        order: this._fileRefs.value.length,
        path: `local:${file.name}`,
        local: true,
        blobPath: blobPath
      });
      this.fileMap.set(newFileRef.path, new FileReference(newFileRef));
      //console.warn('Added file ref', newFileRef, this.fileMap);
      currentFileRefs.push(newFileRef);
      this.cd.detectChanges();
      //console.log('File map', this.fileMap);
    }
    //console.log('New file refs', currentFileRefs);
    this._fileRefs.next(currentFileRefs);
  }

  updateFileRefs(updatedFileRefs: IFileReference[]) {
    const currentFileRefs = this.newFileRefs;
    const newFileRefs = updatedFileRefs.filter(
      (x) => !x['id'] || currentFileRefs.findIndex((current) => current['id'] === x['id']) < 0
    );
    // console.warn('Current file refs', currentFileRefs, newFileRefs);
    newFileRefs.forEach((newFileRef) => {
      // console.warn('Looking for', newFileRef, currentFileRefs);
      //look up by id encase the file name was changed on save
      const existingFileRef = currentFileRefs.find((x) => x['id'] === newFileRef['id']);
      const existingFile = this.fileUpload.files.find((x) => x.name === existingFileRef?.name);
      // console.warn('Found ', existingFile);
      if (existingFile && existingFileRef) {
        // console.warn('Match file refs', newFileRef, existingFile);
        if (existingFileRef) {
          newFileRef['id'] = existingFileRef['id'];
        }
        newFileRef['objectURL'] = existingFile['objectURL'];
        newFileRef['local'] = true;
        this.cancelUpload(null, existingFile.name);
      }
    });
    //clearing all of the pending uploads out, above was missing stuff on Iphone camera upload
    this.fileUpload.files.forEach((element) => {
      this.fileMap.delete(element.name);
    });
    this.fileUpload.files = [];
    this.fileUpload.cd.detectChanges();

    // console.warn('New file refs', newFileRefs);
    // console.warn('Current file refs', currentFileRefs);
    const fileRefs = [...currentFileRefs, ...newFileRefs];
    // console.warn('All file refs', fileRefs);
    this._fileRefs.next(fileRefs);
  }

  get newFileRefs(): IFileReference[] {
    return this._fileRefs.value?.filter((x) => x.isLocal);
  }

  public fileRefsUpdated() {
    //console.log('File refs updated', this._fileRefs.value);
    this._fileRefs.next(this._fileRefs.value);
    this.cd.detectChanges();
  }

  cancelUpload($event: Event | null, file: SummitFile | string) {
    $event?.preventDefault();
    //console.log('Cancel upload', file, this.fileUpload.files);
    let removeIndex = isString(file)
      ? this.fileUpload.files.findIndex((x) => x.name === file)
      : this.fileUpload.files.findIndex((x) => x['objectURL']?.toString() === file.objectURL?.toString());

    if (removeIndex > -1) {
      //console.log('Removing from upload');
      this.fileUpload.files.splice(removeIndex, 1);
    }
    removeIndex = isString(file)
      ? this._fileRefs.value.findIndex((x) => x.name === file)
      : this._fileRefs.value.findIndex((x) => x.name === file.name);

    //console.log('Removing from FileRefs', removeIndex, file.path, this._fileRefs.value);
    if (removeIndex > -1) {
      //console.log('Removing from FileRefs');
      this._fileRefs.value.splice(removeIndex, 1);
      this._fileRefs.next(this._fileRefs.value);
    }
    this.fileCountChange.emit(this._fileRefs.value.length + this.fileUpload.files.length);
  }

  remove($event: Event | null, filePath: string) {
    $event?.preventDefault();
    const index = this._fileRefs.value.findIndex((x) => x.path === filePath || x.name == filePath);
    if (index > -1) {
      const oldValue = this._fileRefs.value[index];
      const newFileRefs = [...this._fileRefs.value];
      newFileRefs.splice(index, 1);
      //console.log('Removing file', filePath, index, oldValue, newFileRefs);
      this._fileRefs.next(newFileRefs);
      this.removeEvent.emit(oldValue.path);
    }
  }

  public deleteFile(file: IFileReference) {
    const isNewFileRef = this._fileRefs.value.findIndex((x) => x.path !== file.path) >= 0;
    if (isNewFileRef) {
      this.cancelUpload(null, file.name);
    } else {
      this.remove(null, file.path);
    }
  }

  public cancelFile(file: File) {
    //console.log('cancel Upload', file, this.fileUpload.files);
    this.cancelUpload(null, file.name);
    this.fileUpload.cd.detectChanges();
  }

  launchFile(file: IFileReference) {
    switch (file.fileType) {
      case FileType.PDF:
        this.launchPDF(null, file);
        break;
      case FileType.PNG:
      case FileType.JPG:
        this.loadFile(null, file.path);
        break;
      default:
        console.error('Unknown file type', file.fileType);
        break;
    }
  }

  private launchBlob(data: Blob) {
    const urlDirect = window.URL.createObjectURL(data);
    window.open(urlDirect, '_blank');
    window.open(urlDirect);
    // this.dialogService.open(SummitFileUploadComponent, {

    // });
  }

  public getBlobPath(fileRef: IFileReference): SafeUrl | undefined {
    if (!fileRef?.path) return 'assets/icons/baseline_flag_white_24dp.png';
    const fileUploadBlobPath = this.fileMap.get(fileRef.path)?.blobPath as string;
    if (!fileUploadBlobPath) return 'assets/icons/baseline_flag_white_24dp.png';
    return (
      this.sanitizer.bypassSecurityTrustResourceUrl(fileUploadBlobPath) ?? 'assets/icons/baseline_flag_white_24dp.png'
    );
  }

  public getImageUrl(file: SummitFile): SafeUrl {
    let fileUrl = file.objectURL;
    if (!fileUrl) {
      //console.warn('Getting image url', file, this._fileRefs.value);
      //look on new files to upload
      const matching = this._fileRefs.value.filter((x) => x?.name === file.name);
      if (matching.length == 1) {
        fileUrl = matching[0].blobPath as string;
      } else {
        //console.error('Could not Find Local File to Preview.  Please Save the Form First then it will work.');
      }
    }
    //console.warn('Getting image url', fileUrl, this._fileRefs.value);
    if (!fileUrl) return 'assets/icons/baseline_flag_white_24dp.png';
    return this.sanitizer.bypassSecurityTrustResourceUrl(fileUrl);
  }

  launchPDF($event, fileRef: IFileReference) {
    //console.log('launchPDF', fileRef, this._fileRefs.value);
    let filePath = this.fileMap.get(fileRef.path)?.blobPath;
    if (!filePath) {
      //look on new files to upload
      const matching = this._fileRefs.value.filter((x) => x.name === fileRef.name && x.isLocal);
      if (matching.length == 1) {
        filePath = matching[0].blobPath;
      } else {
        console.error('Could not Find Local File to Preview.  Please Save the Form First then it will work.');
      }
    }
    $event?.preventDefault();
    if (!this.disablePdfPreview) {
      this.dialogService.open(SFormPdfPreviewDialogComponent, {
        showHeader: true,
        header: fileRef.name,
        closable: true,
        data: filePath,
        height: '80vh',
        width: '95vw',
        keepInViewport: true,
        dismissableMask: true
      });
    } else {
      //console.log('Show disclaimer');
      this.messageService.add({ severity: 'info', summary: 'Info', detail: 'Preview is already open.' });
    }
  }
  loadFile($event, filePath: string) {
    $event?.preventDefault();
    this.crud
      .getFile('tenant', filePath)
      .pipe(take(1))
      .subscribe((data: Blob | undefined) => {
        //console.log('Got file:', data);
        if (!data) return;
        this.launchBlob(data);
      }); // No Unsubscribe
  }
  downloadFile($event, file: IFileReference) {
    //console.log('downloading file ref');
    $event?.preventDefault();
    const link = document.createElement('a');
    const tempFileRef = this.fileMap.get(file.path);
    //console.log('file ref', tempFileRef);
    if (!tempFileRef) return;
    link.href = tempFileRef.blobPath as string;
    link.download = tempFileRef.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
  launchTab($event, file: IFileReference) {
    $event?.preventDefault();
    const tempFileRef = this.fileMap.get(file.path);
    if (!tempFileRef?.blobPath) return;
    this.httpClient.get(tempFileRef.blobPath as string, { responseType: 'blob' }).subscribe((data) => {
      window.open(window.URL.createObjectURL(data));
    });
  }
  updateContentType(fileRef: IFileReference, $event: FileContentType) {
    //console.log('Changing content type', $event, fileRef);
    if (isString($event)) {
      $event = parseInt($event, 10);
    }
    fileRef.contentType = $event;
  }
  updateUploadContentType(fileRef: File | IFileReference, $event: FileContentType) {
    $event = parseInt($event.toString());
    const selFileRef = this._fileRefs.value.find(
      (x) => x.name === fileRef.name && x.contentType === FileContentType.Attachment
    );
    if (selFileRef) {
      //console.log(`Changed type from ${selFileRef.contentType} to ${$event}`);
      selFileRef.contentType = $event;
      this._fileRefs.next(this._fileRefs.value);
    } else {
      //console.log(`No File found ${selFileRef?.contentType} queuing retry`, fileRef);
      setTimeout(() => {
        this.updateUploadContentType(fileRef, $event);
      }, 50);
      return;
    }
    const control = this.contentTypeInputs.find((x) => x.id === fileRef.name);
    //console.log('Control', control, this.contentTypeInputs);
    if (control) {
      control.nonFormValue = $event.toString();
    }
  }

  preventDefault($event) {
    $event.preventDefault();
  }
  getThumbnail(file: IFileReference): void {
    //console.log('file ref', file);
    if (this.fileMap.has(file.path)) {
      //console.log('Already have file ref', file.path);
      return;
    }
    this.crud.getFile('tenant', encodeURIComponent(file.path)).subscribe({
      next: (data) => {
        if (!data) return;
        this.createImageFromBlob(file, data);
      },
      error: (response) => {
        console.log('POST - getThumbnail - in error', response);
      },
      complete: () => {
        //console.log('POST - getThumbnail - observable is now completed.');
      }
    });
  }
  createImageFromBlob(file: IFileReference, image: Blob) {
    const reader = new FileReader();
    reader.addEventListener(
      'load',
      () => {
        this.fileMap.set(file.path, { ...file, blobPath: reader.result as string });
        this.cd.detectChanges();
      },
      false
    );
    if (image) {
      reader.readAsDataURL(image);
    }
  }

  public fileUploaded(event, index) {
    this.fileUpload[index] = event;
  }

  drop(_event) {
    console.log('drop', _event);
    this.clearOldFileListeners();
  }

  public getFiles(): File[] {
    return this.fileUpload.files;
  }

  public getFileReference(fileName: string): IFileReference | undefined {
    return this._fileRefs.value.find((x) => x.name === fileName);
  }

  public addFile(file: File) {
    // this.fileRefs.push(
    //   new FileReference({ name: file.name, path: path, contentType: contentType, fileType: FileType.PNG })
    // );
    this.fileUpload.files.push(file);
    this.addFileRef([file]);
    this.cd.detectChanges();
    //this.fileUpload.cd.detectChanges();
    //this.fileCountChanged(null);
  }

  public detectChanges() {
    this.fileUpload.cd.detectChanges();
    this.cd.detectChanges();
  }

  private clearOldFileListeners() {
    this.fileListenerReset.next([]); //clear all listeners
  }
  private fileListenerReset: Subject<IFileReference[]> = new Subject<IFileReference[]>();
  public async choose(): Promise<IFileReference[]> {
    this.clearOldFileListeners();
    //pre-listening for new file refs to be added, then filter out any that were already there
    var observableHolder = firstValueFrom(
      // merge(
      //this.fileListenerReset,
      this._fileRefs.pipe(
        // tap((fileRefs) => console.warn('Choose: File refs changed', fileRefs)),
        map((fileRefs) => fileRefs?.filter((x) => x.isLocal) ?? []),
        // tap((fileRefs) => console.warn('Choose: File refs local', fileRefs)),
        pairwise(),
        skipWhile(([prev, curr]: [IFileReference[], IFileReference[]]) => prev.length === curr.length),
        map(
          ([prev, curr]: [IFileReference[], IFileReference[]]) =>
            curr?.filter((x) => !prev.some((p) => p.path === x.path)) ?? []
        ),
        map((x) => x.sort((a, b) => a.order - b.order))
      )
      // )
    );
    this.fileUpload.choose();
    // Remove fallback polling for Safari/iOS, use only observable approach
    return observableHolder;
  }

  override ngOnDestroy(): void {
    if (this.fileInputListener) {
      this.fileInputListener();
    }

    super.ngOnDestroy();
  }

  refresh() {
    this.refreshEvent.emit();
  }
}
